import React, { useEffect, useState, useMemo } from 'react'
import { DialogContent, MenuItem, Typography, Icon, IconButton, useTheme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import StyledDialog from '../../Common/Dialogs/StyledDialog'
import StyledTextField from '../../Common/StyledTextField'
import StyledKeyboardTimePicker from '../../Common/StyledKeyboardTimePicker'
import StyledSwitchWithLabel from '../../Common/StyledSwitchWithLabel'
import StyledAlert from '../../Common/StyledAlert'
import { MainButton } from '../../Button/MainButton'
import {
	DIALOG_EDIT_MODES,
	TRAINING_CLASS_TEMPLATE,
	ACCESS_TYPES,
	NON_BREAKING_SPACE,
	NUMBER_OF_SESSIONS,
	STANDARD_DATE_FORMAT,
	getClassStatus,
	TRAINING_COURSE_CATEGORY
} from '../../../utils'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import DatePicker from 'react-multi-date-picker'
import { gray, purple } from '../../../utils/colors'
import { FONT_WEIGHTS } from '../../../utils/enums'
import moment from 'moment'
import DangerConfirmationDialog from '../../Common/Dialogs/DangerConfirmation'
import TrainingEnrollmentDialog from './TrainingEnrollmentDialog'
import { AddOutlined } from '@material-ui/icons'

const useStyles = makeStyles(() => ({
	dialogContent: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		padding: '0 1.5rem',
		gap: '1rem',
		flex: 1
	},
	rowWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem'
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		'& > div': {
			flex: 1
		},
		gap: '1.5rem'
	},
	dialogActions: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		gap: '1rem',
		margin: '2rem 0 1rem 0',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		},
		justifySelf: 'flex-end'
	},
	abbrAdornment: {
		marginLeft: '10px',
		marginRight: 0,
		marginTop: '19px !important',
		'& > p': {
			fontSize: '1rem'
		}
	},
	datesPickerContainer: {
		display: 'flex',
		flexDirection: 'column',
		'& .rmdp-container': {
			width: '100%',
			'& > input': {
				padding: '0 10px !important'
			},
			'& .rmdp-week-day': {
				color: purple[500]
			},
			'& .rmdp-day.rmdp-selected span:not(.highlight)': {
				backgroundColor: purple[500],
				boxShadow: 'none'
			},
			'& .rmdp-button.rmdp-action-button': {
				color: purple[500]
			},
			'& .rmdp-arrow-container:hover': {
				backgroundColor: purple[500],
				boxShadow: 'none'
			},
			'& .rmdp-arrow-container.disabled:hover': {
				backgroundColor: 'transparent'
			},
			'& .rmdp-day:not(.rmdp-disabled):not(.rmdp-day-hidden) span:hover': {
				backgroundColor: purple[200]
			}
		}
	},
	maxCapacityContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '.5rem'
	},
	sectionHeader: {
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '0.75rem',
		textTransform: 'uppercase',
		letterSpacing: '0.047rem'
	},
	attendeesHeaderContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: '.25rem'
	},
	attendeesContainer: {
		overflow: 'scroll',
		maxHeight: '7rem',
		border: `1px solid ${gray[400]}`,
		borderRadius: '.375rem',
		padding: '1rem',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start'
	},
	attendee: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: '.4rem',
		'& > span': {
			fontSize: '1rem',
			fontWeight: 600
		}
	}
}))

/**
 *
 * @param {{
  open: Boolean, 
	onClose: Function, 
	onCancelled: Function,
	title: String,
	mode: "new" | "edit", 
	trainingClass: TRAINING_CLASS_TEMPLATE
}} params
 * @returns
 */
const TrainingClassDialog = ({
	open,
	onSaved,
	onCancelled,
	title = 'Area',
	mode = DIALOG_EDIT_MODES.New,
	trainingClass = {}
}) => {
	const classes = useStyles()
	const theme = useTheme()

	const [editedTrainingClass, setEditedTrainingClass] = useState(trainingClass)
	const [editedKeys, setEditedKeys] = useState(new Set())
	const [errors, setErrors] = useState([])
	const [loading, setLoading] = useState(false)
	const [confirmRemoveAttendeeDialogOpen, setConfirmRemoveAttendeeDialogOpen] = useState(false)
	const [attendeeToRemove, setAttendeeToRemove] = useState(null)
	const [addAttendeeDialogOpen, setAddAttendeeDialogOpen] = useState(false)
	const [originalStartDate, setOriginalStartDate] = useState(null)

	const {
		facility,
		trainingCourses,
		trainingLocations,
		trainingStaff,
		saveTrainingClassForFacility,
		updateTrainingClassForFacility,
		getTrainingClassNextAvailableId,
		cancelEnrollmentForClass
	} = useSelectedFacility()

	useEffect(() => {
		setEditedTrainingClass(trainingClass)
		setOriginalStartDate(trainingClass?.dates?.[0])
	}, [trainingClass, setEditedTrainingClass])

	const classStatus = useMemo(() => {
		if (
			!trainingClass ||
			!facility ||
			!trainingClass.dates ||
			trainingClass.dates.length < 1 ||
			!trainingClass.start_time ||
			!trainingClass.end_time
		) {
			return null
		}

		return getClassStatus(trainingClass, facility)
	}, [trainingClass, facility])

	const handleResetDialog = () => {
		if (mode === DIALOG_EDIT_MODES.New) {
			setEditedTrainingClass({ ...TRAINING_CLASS_TEMPLATE })
		}
		setEditedKeys(new Set())
		setErrors([])
	}

	const handleOnCancelled = () => {
		if (onCancelled) {
			handleResetDialog()
			onCancelled()
		}
	}

	const handleAddAttendeeRequest = () => {
		setAddAttendeeDialogOpen(true)
	}
	const handleCancelAddAttendeeRequest = () => {
		setAddAttendeeDialogOpen(false)
	}
	const handleRemoveAttendeeRequest = (attendee) => {
		setAttendeeToRemove(attendee)
		setConfirmRemoveAttendeeDialogOpen(true)
	}

	const handleCancelRemoveAttendeeRequest = () => {
		setConfirmRemoveAttendeeDialogOpen(false)
		setTimeout(() => {
			setAttendeeToRemove(null)
		}, 500)
	}

	const handleRemoveAttendee = async () => {
		const { values } = attendeeToRemove || {}
		const { handler_ids, pet_id } = values
		if (!handler_ids || !pet_id) {
			return
		}

		const { _id } = trainingClass

		setLoading(true)

		await cancelEnrollmentForClass({
			mongo_class_id: _id,
			facility_id: facility._id,
			handler_ids,
			pet_id
		})

		setLoading(false)

		handleCancelRemoveAttendeeRequest()
	}

	const handleOnSave = async () => {
		const payload = {
			...editedTrainingClass
		}

		setErrors([])
		setLoading(true)

		let response = {}

		if (mode === DIALOG_EDIT_MODES.New) {
			response = await saveTrainingClassForFacility({
				trainingClass: payload,
				facility_id: facility._id
			})
		}

		if (mode === DIALOG_EDIT_MODES.Edit && editedKeys.size > 0) {
			const updates = {}
			for (const key of editedKeys) {
				updates[key] = editedTrainingClass[key]
			}
			const { _id } = editedTrainingClass
			response = await updateTrainingClassForFacility({
				mongo_class_id: _id,
				updates,
				facility_id: facility._id
			})
			setEditedKeys(new Set())
		}

		setLoading(false)

		if (onSaved && response?.success === true) {
			handleResetDialog()
			onSaved()
		}

		if (response?.success === false && response.errors) {
			setErrors(response.errors)
		}
	}

	const fetchNextAvailableClassId = async (training_course_id) => {
		const nextId = await getTrainingClassNextAvailableId({ course_id: training_course_id })
		const course = trainingCourses.find(({ _id }) => _id === training_course_id)
		handleFieldUpdate({ field: 'class_id', targetValue: `${course.course_id}-${nextId}` })
	}

	const handleFieldUpdate = async ({ field, targetValue }) => {
		const [mainField, subField] = field.split('.')

		setEditedTrainingClass((prev) => {
			const revised = { ...prev }
			if (subField) {
				revised[mainField] = { ...revised[mainField], [subField]: targetValue }
			} else {
				revised[field] = targetValue
			}

			if (field === 'training_course_id') {
				const course = trainingCourses.find(({ _id }) => _id === targetValue)
				revised.number_of_sessions = course?.number_of_sessions
				revised.capacity_max = course?.capacity_max
				revised.capacity_max_override = false
				fetchNextAvailableClassId(targetValue)
				if (course?.categories?.includes(TRAINING_COURSE_CATEGORY.Private)) {
					revised.access = ACCESS_TYPES.AdminOnly
				} else {
					revised.access = ''
				}
			}

			if (field === 'capacity_max_override') {
				if (targetValue === false) {
					const { training_course_id } = prev
					const course = trainingCourses.find(({ _id }) => _id === training_course_id)
					revised.capacity_max = course?.capacity_max
				}
			}

			if (field === 'number_of_sessions_override') {
				if (targetValue === true) {
					revised.access = ACCESS_TYPES.AdminOnly
				} else {
					revised.access = ''
					const { training_course_id } = prev
					const course = trainingCourses.find(({ _id }) => _id === training_course_id)
					revised.number_of_sessions = course?.number_of_sessions
					if (course?.categories?.includes(TRAINING_COURSE_CATEGORY.Private)) {
						revised.access = ACCESS_TYPES.AdminOnly
					}
				}
			}

			if (field === 'start_time') {
				const { training_course_id } = prev
				const course = trainingCourses.find(({ _id }) => _id === training_course_id)
				if (!course) {
					revised.end_time = ''
				}

				if (targetValue && /^\d{2}:\d{2}$/.test(targetValue)) {
					const { session_length } = course
					const [hours, minutes] = targetValue.split(':')
					const start = moment().hours(hours).minutes(minutes)
					const end = start.add(session_length, 'minutes')
					revised.end_time = end.format('HH:mm')
				}
			}
			return revised
		})
		setEditedKeys((keys) => keys.add(mainField))
		if (mainField === 'start_time') {
			setEditedKeys((keys) => keys.add('end_time'))
		}
	}

	const trainingCourseName = useMemo(() => {
		const { training_course_id } = editedTrainingClass
		const course = trainingCourses.find(({ _id }) => _id === training_course_id)
		return course?._id
	}, [editedTrainingClass, trainingCourses])

	const trainingLocationName = useMemo(() => {
		const { training_location_id } = editedTrainingClass
		const location = trainingLocations.find(({ _id }) => _id === training_location_id)
		return location?._id
	}, [editedTrainingClass, trainingLocations])

	const canSave = useMemo(() => {
		let canMoveForward =
			editedKeys.size > 0 &&
			editedTrainingClass.training_course_id > '' &&
			editedTrainingClass.class_id > '' &&
			editedTrainingClass.access > '' &&
			editedTrainingClass.dates.length > 0 &&
			editedTrainingClass.dates.length === editedTrainingClass.number_of_sessions &&
			editedTrainingClass.start_time > '' &&
			editedTrainingClass.training_location_id > '' &&
			editedTrainingClass.instructor_id > ''

		if (mode === DIALOG_EDIT_MODES.Edit) {
			canMoveForward = editedKeys.size > 0
		}

		return canMoveForward
	}, [editedTrainingClass, mode, editedKeys])

	const classIsAtMaxCapacity = useMemo(() => {
		return trainingClass?.attendees?.size >= trainingClass?.capacity_max
	}, [trainingClass])

	const currentEnrolledHandlerIds = useMemo(() => {
		const set = new Set()
		if (!trainingClass || !trainingClass.attendees) {
			return set
		}

		trainingClass.attendees.forEach((attendee) => {
			attendee.handler_ids.forEach((handler_id) => {
				set.add(handler_id)
			})
		})

		return set
	}, [trainingClass])

	const currentEnrolledPetIds = useMemo(() => {
		const set = new Set()
		if (!trainingClass || !trainingClass.attendees) {
			return set
		}

		trainingClass.attendees.forEach((attendee) => {
			set.add(attendee.pet_id)
		})

		return set
	}, [trainingClass])

	const disableDateEditing = useMemo(() => {
		return (
			mode === DIALOG_EDIT_MODES.ViewOnly ||
			// mode === DIALOG_EDIT_MODES.Edit ||
			classStatus?.isBetween ||
			classStatus?.isAfter ||
			editedTrainingClass.number_of_sessions < 1 ||
			editedTrainingClass.number_of_sessions === undefined ||
			editedTrainingClass.has_existing_adjustments === true ||
			editedTrainingClass.has_transfers === true
		)
	}, [mode, classStatus, editedTrainingClass])

	const disabledEditingInfo = useMemo(() => {
		const { has_existing_adjustments, has_transfers } = editedTrainingClass
		if (has_existing_adjustments) {
			return "Dates and times aren't editable once an individual class session has been edited. You'll need to edit each session individually for this class."
		}
		if (has_transfers) {
			return "Dates aren't editable once a transfer to an individual class session has been made. You'll need to edit each session individually for this class."
		}
		if (has_existing_adjustments && has_transfers) {
			return "Dates and times aren't editable once an individual class session has been edited and a transfer has been made. You'll need to edit each session individually for this class."
		}
		return ''
	}, [editedTrainingClass])

	const canOverrideNumberOfSessions = useMemo(() => {
		const course = trainingCourses.find(({ _id }) => _id === editedTrainingClass.training_course_id)
		if (!course) {
			return false
		}

		return course?.categories?.includes(TRAINING_COURSE_CATEGORY.Private)
	}, [editedTrainingClass])

	const isPrivateClass = useMemo(() => {
		const course = trainingCourses.find(({ _id }) => _id === editedTrainingClass.training_course_id)
		if (!course) {
			return false
		}

		return course?.categories?.includes(TRAINING_COURSE_CATEGORY.Private)
	}, [editedTrainingClass])

	return (
		<>
			<StyledDialog open={open} onClose={handleOnCancelled} title={title} showActivityIndicator={loading}>
				<DialogContent>
					<div className={classes.dialogContent}>
						<div className={classes.rowWrapper}>
							<div className={classes.row}>
								<div>
									<StyledTextField
										variant="filled"
										label="Course Type"
										header="Course Type"
										select
										required
										value={trainingCourseName}
										disabled={
											mode === DIALOG_EDIT_MODES.ViewOnly || mode === DIALOG_EDIT_MODES.Edit
										}
										onChange={(e) => {
											handleFieldUpdate({
												field: 'training_course_id',
												targetValue: e.target.value
											})
										}}>
										{trainingCourses
											.filter(
												({ categories = [] }) =>
													!categories.includes(
														TRAINING_COURSE_CATEGORY.FacilityVisitSession
													)
											)
											.map(({ _id, name }) => {
												return (
													<MenuItem key={_id} value={_id}>
														{name}
													</MenuItem>
												)
											})}
									</StyledTextField>
								</div>
								<div>
									<StyledTextField
										variant="filled"
										header="Class ID"
										label="Class ID"
										disabled
										value={editedTrainingClass?.class_id || ''}
									/>
								</div>
							</div>
							<div className={classes.row}>
								<div className={classes.maxCapacityContainer}>
									<StyledTextField
										variant="filled"
										label="Number of Sessions"
										header="Number of Sessions"
										select
										disabled={!editedTrainingClass?.number_of_sessions_override}
										value={editedTrainingClass?.number_of_sessions || ''}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'number_of_sessions',
												targetValue: e.target.value
											})
										}>
										{NUMBER_OF_SESSIONS.map((number) => {
											return (
												<MenuItem key={`session_number-${number}`} value={number}>
													{number}
												</MenuItem>
											)
										})}
									</StyledTextField>
									{canOverrideNumberOfSessions ? (
										<div>
											<StyledSwitchWithLabel
												label="Override Number of Sessions"
												checked={editedTrainingClass?.number_of_sessions_override}
												onChecked={(e) => {
													handleFieldUpdate({
														field: 'number_of_sessions_override',
														targetValue: e.target.checked
													})
												}}
											/>
										</div>
									) : null}
								</div>
								<div>
									<StyledTextField
										variant="filled"
										select
										required
										header="Access"
										label="Access"
										disabled={
											mode === DIALOG_EDIT_MODES.ViewOnly ||
											editedTrainingClass?.number_of_sessions_override ||
											isPrivateClass
										}
										value={editedTrainingClass?.access}
										tooltip={{
											tooltipChildren: (
												<div>
													<p>Global Access: Enrollment available to public</p>
													<p>Admin Only: Enrollment by admin only</p>
												</div>
											),
											faIcon: 'fa-info-circle'
										}}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'access',
												targetValue: e.target.value
											})
										}>
										{Object.values(ACCESS_TYPES).map((access, i) => {
											return (
												<MenuItem key={`access-type-${i}`} value={access}>
													{access}
												</MenuItem>
											)
										})}
									</StyledTextField>
								</div>
							</div>
							<div className={classes.row}>
								<div className={classes.maxCapacityContainer}>
									<StyledTextField
										variant="filled"
										label="Capacity Maximum"
										header="Capacity Maximum"
										disabled={!editedTrainingClass?.capacity_max_override}
										value={editedTrainingClass?.capacity_max || ''}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'capacity_max',
												targetValue: e.target.value
											})
										}
									/>
									<div>
										<StyledSwitchWithLabel
											label="Override Maximum Capacity"
											checked={editedTrainingClass?.capacity_max_override}
											onChecked={(e) => {
												handleFieldUpdate({
													field: 'capacity_max_override',
													targetValue: e.target.checked
												})
											}}
										/>
									</div>
								</div>
								<div>{NON_BREAKING_SPACE}</div>
							</div>
							<div className={classes.row}>
								<div className={classes.datesPickerContainer}>
									{/* 
										! disable date changing for now after class is created (edit mode) !
									*/}
									<Typography variant="overline">Dates</Typography>
									<DatePicker
										required
										multiple={true}
										offsetX={0}
										offsetY={0}
										zIndex={10000}
										minDate={
											originalStartDate
												? moment(originalStartDate).toDate()
												: moment().toDate()
										}
										disabled={disableDateEditing}
										value={editedTrainingClass.dates}
										onChange={(dateArray) => {
											handleFieldUpdate({
												field: 'dates',
												targetValue: dateArray
													.sort((a, b) => a - b)
													.map((date) => date.format(STANDARD_DATE_FORMAT))
											})
										}}
										placeholder="Dates *"
										format={STANDARD_DATE_FORMAT}
										// mapDays={() => 'day'}
										style={{
											width: '100%',
											height: 56,
											margin: 0,
											borderBottomLeftRadius: 0,
											borderBottomRightRadius: 0,
											backgroundColor: disableDateEditing ? gray[200] : gray[100],
											border: `1px solid ${gray[500]}`,
											paddingLeft: 16,
											fontSize: '1rem',
											fontFamily: 'Nunito',
											color: disableDateEditing ? gray[600] : gray[800]
										}}
									/>
								</div>
								<div style={{ display: 'flex', alignItems: 'flex-end' }}>
									{disabledEditingInfo > '' ? (
										<StyledAlert type="info" text={disabledEditingInfo} />
									) : (
										NON_BREAKING_SPACE
									)}
								</div>
							</div>
							<div className={classes.row}>
								<div>
									<StyledKeyboardTimePicker
										disableFuture
										ampm={false}
										format="HH:mm"
										required
										label="Class Start Time"
										header="Class Start Time (24hr format)"
										initialvalue={editedTrainingClass.start_time}
										disabled={
											mode === DIALOG_EDIT_MODES.ViewOnly ||
											editedTrainingClass.training_course_id === '' ||
											editedTrainingClass.has_existing_adjustments === true
										}
										timeChanged={(e) => {
											const { formatted } = e
											handleFieldUpdate({
												field: 'start_time',
												targetValue: formatted
											})
										}}
									/>
								</div>
								<div>
									<StyledKeyboardTimePicker
										disableFuture
										ampm={false}
										format="HH:mm"
										label="Class End Time"
										header="Class End Time"
										initialvalue={editedTrainingClass.end_time}
										disabled
										required
									/>
								</div>
							</div>
							<div className={classes.row}>
								<div>
									<StyledTextField
										variant="filled"
										label="Location"
										header="Location"
										select
										value={trainingLocationName}
										required={true}
										disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'training_location_id',
												targetValue: e.target.value
											})
										}>
										{trainingLocations.map(({ _id, name, course_id }) => {
											return (
												<MenuItem key={_id} value={_id}>
													{name}
												</MenuItem>
											)
										})}
									</StyledTextField>
								</div>
								<div>
									<StyledTextField
										variant="filled"
										label="Instructor"
										header="Instructor"
										select
										value={editedTrainingClass.instructor_id}
										required={true}
										disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'instructor_id',
												targetValue: e.target.value
											})
										}>
										{trainingStaff.map(({ _id, name }) => {
											return (
												<MenuItem key={_id} value={_id}>
													{name}
												</MenuItem>
											)
										})}
									</StyledTextField>
								</div>
							</div>
							{mode === DIALOG_EDIT_MODES.Edit ? (
								<div>
									<div className={classes.attendeesHeaderContainer}>
										<Typography variant="h3" className={classes.sectionHeader}>
											Attendees ({trainingClass?.attendees?.size} /{' '}
											{trainingClass?.capacity_max} Enrolled)
										</Typography>
										<MainButton
											label="Add Attendee"
											variant="text"
											disabled={classIsAtMaxCapacity}
											onClick={handleAddAttendeeRequest}
											style={{ padding: `0 .5rem`, fontSize: '.85rem' }}
											startIcon={
												<AddOutlined
													style={{
														marginRight: 0,
														fill: theme.palette.secondary.main
													}}
												/>
											}
										/>
									</div>
									<div className={classes.attendeesContainer}>
										{trainingClass?.attendees
											? [...trainingClass.attendees.entries()].map(([id, values]) => (
													<div className={classes.attendee} key={id}>
														<IconButton
															onClick={() =>
																handleRemoveAttendeeRequest({ id, values })
															}>
															<Icon
																className="fas fa-minus-circle"
																style={{
																	fontSize: '1rem',
																	overflow: 'unset',
																	color: 'red'
																}}
															/>
														</IconButton>
														<Typography variant="inherit">
															{values.handler_names.join(', ')} ({values.pet_name})
														</Typography>
													</div>
											  ))
											: null}
									</div>
								</div>
							) : null}
						</div>
						{errors.length > 0 ? (
							<div>
								{errors.map((error, i) => {
									const { message = 'Error Saving' } = error
									return <StyledAlert key={`error-${i}`} type="error" text={message} />
								})}
							</div>
						) : null}
						<div className={classes.dialogActions}>
							{mode !== DIALOG_EDIT_MODES.ViewOnly ? (
								<MainButton
									label={mode === DIALOG_EDIT_MODES.New ? 'SAVE' : 'UPDATE'}
									onClick={handleOnSave}
									variant="contained"
									disabled={!canSave}
								/>
							) : null}
							<MainButton label="Cancel" onClick={handleOnCancelled} />
						</div>
					</div>
				</DialogContent>
			</StyledDialog>
			<TrainingEnrollmentDialog
				open={addAttendeeDialogOpen}
				onClose={handleCancelAddAttendeeRequest}
				onSaved={handleCancelAddAttendeeRequest}
				onCancelled={handleCancelAddAttendeeRequest}
				title="Add Attendee"
				showActivityIndicator={loading}
				currentEnrolledPetIds={currentEnrolledPetIds}
				currentEnrolledHandlerIds={currentEnrolledHandlerIds}
				trainingClass={trainingClass}
			/>
			<DangerConfirmationDialog
				title="Remove Attendee"
				open={confirmRemoveAttendeeDialogOpen}
				description={
					<span>
						This will remove{' '}
						{attendeeToRemove?.values?.handler_names?.length > 1 ? (
							<strong>{attendeeToRemove?.values.handler_names.join(', ')}</strong>
						) : (
							<strong>{attendeeToRemove?.values?.primary_handler?.full_name} </strong>
						)}{' '}
						and <strong>{attendeeToRemove?.values?.pet_name}</strong> from this <strong>class</strong>.{' '}
						{attendeeToRemove?.values?.primary_handler?.first_name} will not be able to re-enroll{' '}
						{attendeeToRemove?.values?.pet_name} unless a spot is available. Refunds are not
						automatically applied at this time and will need to be manually processed for the client,
						according to the cancellation policy.
					</span>
				}
				dangerButtonText="Confirm"
				onConfirmDangerousAction={handleRemoveAttendee}
				onCancel={handleCancelRemoveAttendeeRequest}
			/>
		</>
	)
}

export default TrainingClassDialog
